"use client";

import { useId, useRef } from "react";
import { MapPin } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useAddressSuggestions,
  type AddressSuggestion,
} from "./use-address-suggestions";
import {
  PlacesProvider,
  usePlacesContext,
} from "@/components/providers/PlacesProvider";
import { useHasMounted } from "@/hooks/use-has-mounted";
import type { ParsedAddress } from "./address-types";

export type { ParsedAddress } from "./address-types";

export interface AddressAutocompleteProps {
  id?: string;
  value: string;
  onChange: (displayValue: string, parsed: ParsedAddress | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  compact?: boolean;
  variant?: "ghost";
  hideIcon?: boolean;
  regionCodes?: string[];
  onResolvingChange?: (resolving: boolean) => void;
  onSubmit?: () => void;
  unavailableHint?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-label"?: string;
}

const SUGGESTION_LIST_CLASSES =
  "w-[var(--radix-popper-anchor-width)] min-w-[min(16rem,calc(100vw-2rem))] p-0";

function addressAccessibilityProps(
  props: Pick<AddressAutocompleteProps, "aria-invalid" | "aria-describedby" | "aria-label">,
) {
  return {
    "aria-invalid": props["aria-invalid"],
    "aria-describedby": props["aria-describedby"],
    "aria-label": props["aria-label"],
  };
}

export function FallbackInput(props: AddressAutocompleteProps) {
  const {
    id,
    value,
    onChange,
    onBlur,
    placeholder = "Enter delivery address",
    className,
    inputClassName,
    disabled,
    required,
    autoFocus,
    compact,
    onSubmit,
  } = props;
  return (
    <div className={cn("relative", className)}>
      {props.hideIcon ? null : (
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      )}
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value, null)}
        onBlur={onBlur}
        onKeyDown={(event) => {
          if (event.key === "Enter" && onSubmit) {
            event.preventDefault();
            onSubmit();
          }
        }}
        placeholder={placeholder}
        className={cn(!props.hideIcon && "pl-9", inputClassName)}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        {...addressAccessibilityProps(props)}
        variant={props.variant}
        size={compact ? "compact" : "default"}
      />
    </div>
  );
}

function SuggestionList({
  listId,
  suggestions,
  activeIndex,
  onHighlight,
  onSelect,
}: {
  listId: string;
  suggestions: AddressSuggestion[];
  activeIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (suggestion: AddressSuggestion) => void;
}) {
  return (
    <ul
      id={listId}
      role="listbox"
      className="max-h-64 overflow-y-auto overscroll-contain py-1"
    >
      {suggestions.map((suggestion, index) => {
        const prediction = suggestion.placePrediction;
        if (!prediction) return null;
        return (
          <li
            key={prediction.placeId}
            id={`${listId}-option-${index}`}
            role="option"
            aria-selected={index === activeIndex}
            className={cn(
              "flex min-h-11 cursor-pointer touch-manipulation items-center gap-2 px-3 py-2.5 text-sm",
              index === activeIndex && "bg-accent text-accent-foreground",
            )}
            onMouseEnter={() => onHighlight(index)}
            onPointerDown={(event) => {
              event.preventDefault();
              onSelect(suggestion);
            }}
          >
            <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">
              {prediction.text.text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function LookupUnavailableHint({ hint }: { hint?: string }) {
  return (
    <p className="mt-1 text-xs text-muted-foreground">
      {hint ??
        "Address lookup is unavailable — type your address, then fill in city, state, and ZIP below."}
    </p>
  );
}

export function AddressAutocomplete(props: AddressAutocompleteProps) {
  const { status, requestLoad } = usePlacesContext();

  if (!requestLoad) {
    return <StandaloneAddressAutocomplete {...props} />;
  }

  if (status === "unavailable") {
    return <FallbackInput {...props} />;
  }

  return <AddressAutocompleteLoaded {...props} requestLoad={requestLoad} />;
}

function suggestionInputHandlers(
  { onChange, onSubmit }: Pick<AddressAutocompleteProps, "onChange" | "onSubmit">,
  list: ReturnType<typeof useAddressSuggestions>,
  requestLoad: () => void,
) {
  return {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value, null);
      list.debouncedFetch(event.target.value);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
      list.handleKeyDown(event);
      const canSubmit = !list.isOpen || list.activeIndex < 0;
      if (event.key === "Enter" && onSubmit && canSubmit) {
        event.preventDefault();
        onSubmit();
      }
    },
    onFocus: () => {
      requestLoad();
      if (list.suggestions.length > 0) list.setIsOpen(true);
    },
    onPointerDown: requestLoad,
  };
}

function AddressAutocompleteLoaded({
  requestLoad,
  ...props
}: AddressAutocompleteProps & { requestLoad: () => void }) {
  const { onChange, regionCodes, onResolvingChange } = props;
  const { status } = usePlacesContext();
  const anchorRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const list = useAddressSuggestions({
    onChange,
    regionCodes,
    onResolvingChange,
  });
  const listOpen = list.isOpen && list.suggestions.length > 0;

  return (
    <Popover open={listOpen} onOpenChange={list.setIsOpen}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className={props.className}>
          <div className="relative">
            {props.hideIcon ? null : (
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
            )}
            <SuggestionInput
              {...props}
              listId={listId}
              listOpen={listOpen}
              activeIndex={list.activeIndex}
              {...suggestionInputHandlers(props, list, requestLoad)}
            />
          </div>
          {(list.isUnavailable || status === "failed") && (
            <LookupUnavailableHint hint={props.unavailableHint} />
          )}
        </div>
      </PopoverAnchor>
      <SuggestionPopoverContent anchorRef={anchorRef} listId={listId} list={list} />
    </Popover>
  );
}

function SuggestionPopoverContent({
  anchorRef,
  listId,
  list,
}: {
  anchorRef: React.RefObject<HTMLDivElement | null>;
  listId: string;
  list: ReturnType<typeof useAddressSuggestions>;
}) {
  return (
    <PopoverContent
      className={SUGGESTION_LIST_CLASSES}
      align="start"
      collisionPadding={8}
      onOpenAutoFocus={(event) => event.preventDefault()}
      onCloseAutoFocus={(event) => event.preventDefault()}
      onInteractOutside={(event) => {
        if (
          event.target instanceof Node &&
          anchorRef.current?.contains(event.target)
        ) {
          event.preventDefault();
        }
      }}
    >
      <SuggestionList
        listId={listId}
        suggestions={list.suggestions}
        activeIndex={list.activeIndex}
        onHighlight={list.setActiveIndex}
        onSelect={list.handleSelect}
      />
    </PopoverContent>
  );
}

function SuggestionInput({
  listId,
  listOpen,
  activeIndex,
  onChange,
  onKeyDown,
  onFocus,
  onPointerDown,
  ...props
}: Omit<AddressAutocompleteProps, "onChange"> & {
  listId: string;
  listOpen: boolean;
  activeIndex: number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onPointerDown: () => void;
}) {
  return (
    <Input
      id={props.id}
      value={props.value}
      onChange={onChange}
      onBlur={props.onBlur}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onPointerDown={onPointerDown}
      placeholder={props.placeholder ?? "Enter delivery address"}
      className={cn(!props.hideIcon && "pl-9", props.inputClassName)}
      disabled={props.disabled}
      required={props.required}
      autoFocus={props.autoFocus}
      autoComplete="off"
      role="combobox"
      aria-expanded={listOpen}
      aria-autocomplete="list"
      aria-controls={listOpen ? listId : undefined}
      {...addressAccessibilityProps(props)}
      aria-activedescendant={
        listOpen && activeIndex >= 0
          ? `${listId}-option-${activeIndex}`
          : undefined
      }
      variant={props.variant}
      size={props.compact ? "compact" : "default"}
    />
  );
}

export function StandaloneAddressAutocomplete(props: AddressAutocompleteProps) {
  const hasMounted = useHasMounted();

  if (!hasMounted) {
    return <FallbackInput {...props} />;
  }

  return (
    <PlacesProvider deferred fallback={<FallbackInput {...props} />}>
      <AddressAutocomplete {...props} />
    </PlacesProvider>
  );
}
